import { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Fullscreen image lightbox with keyboard navigation.
 * Props:
 *   images: [{ url, caption?, alt? }]
 *   index: starting index
 *   open: boolean
 *   onClose
 */
export const Lightbox = ({ images = [], index: startIndex = 0, open, onClose }) => {
    const [i, setI] = useState(startIndex);

    useEffect(() => { if (open) setI(startIndex); }, [open, startIndex]);

    const next = useCallback(() => setI((v) => (v + 1) % images.length), [images.length]);
    const prev = useCallback(() => setI((v) => (v - 1 + images.length) % images.length), [images.length]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
            if (e.key === "ArrowRight") next();
            if (e.key === "ArrowLeft") prev();
        };
        document.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [open, next, prev, onClose]);

    if (!open || images.length === 0) return null;
    const item = images[i];

    return (
        <div
            className="fixed inset-0 z-[80] bg-stone-900/95 backdrop-blur-md flex items-center justify-center"
            onClick={onClose}
            data-testid="lightbox"
        >
            <button
                onClick={onClose}
                className="absolute top-5 right-5 p-3 text-white hover:bg-white/10 z-10"
                aria-label="Schließen"
                data-testid="lightbox-close"
            >
                <X size={22} />
            </button>

            {images.length > 1 && (
                <>
                    <button
                        onClick={(e) => { e.stopPropagation(); prev(); }}
                        className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 p-3 sm:p-4 text-white hover:bg-white/10 z-10"
                        aria-label="Vorheriges Bild"
                        data-testid="lightbox-prev"
                    >
                        <ChevronLeft size={24} />
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); next(); }}
                        className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 p-3 sm:p-4 text-white hover:bg-white/10 z-10"
                        aria-label="Nächstes Bild"
                        data-testid="lightbox-next"
                    >
                        <ChevronRight size={24} />
                    </button>
                </>
            )}

            <div
                className="max-w-[95vw] max-h-[92vh] relative"
                onClick={(e) => e.stopPropagation()}
            >
                <img
                    key={item.url}
                    src={item.url}
                    alt={item.alt || item.caption || "Bild"}
                    className="max-w-full max-h-[88vh] object-contain animate-fade-in"
                />
                {item.caption && (
                    <div className="absolute left-0 right-0 bottom-0 px-5 py-3 bg-stone-900/85 backdrop-blur text-white font-body text-sm text-center">
                        {item.caption}
                    </div>
                )}
                <div className="absolute -top-8 left-0 text-white/65 font-body text-xs tracking-[0.2em] uppercase">
                    {i + 1} / {images.length}
                </div>
            </div>
        </div>
    );
};
